import React, { FC, useState } from 'react';
import cls from 'classnames';
import { AmountEntryXlProps } from './AmountEntryXl.types';
import { getClassNames, getRootClassListFromProps } from '../../../utilities/classNames';
import AssistiveText from '../primitives/assistiveText';

export const classNames = getClassNames('sds-amount-entry-xl', [
	'input-container',
	'input-decorator',
	'input-spacer',
	'suffix',
	'suffix-container',
	'variant-currency',
	'variant-percent',
	'assistive-text',
]);

const showPercentStyle = {
	'--percent-display': 'inline-block',
};

const showCurrencyStyle = {
	'--currency-display': 'inline-block',
};

const AmountEntryXl: FC<AmountEntryXlProps> = (props) => {
	const { className, defaultValue = '0', variant, assistiveText } = props;

	const [internalValue, setInternalValue] = useState(defaultValue);
	const [suffix, setSuffix] = useState('');

	function handleOnChange(e: React.ChangeEvent<HTMLInputElement>): void {
		const val = e.currentTarget.value.replace(/[^0-9.]+/g, '');
		if (!val) {
			setInternalValue('0');
			setSuffix('');
			return;
		}

		const [_lhs, rhs] = val.toString().split('.');
		//if mantissa exists, check to determine length of suffix
		if (rhs) {
			if (rhs.length >= 2) {
				setSuffix('');
				setInternalValue(parseFloat(val).toFixed(2));
			} else if (rhs.length === 1) {
				setSuffix('0');
				setInternalValue(parseFloat(val).toFixed(1));
			}
		} else if (val.endsWith('.')) {
			setSuffix('00');
			setInternalValue(val);
		} else {
			setSuffix('');
			setInternalValue(parseInt(val));
		}
	}

	function handleOnInput(e: React.FormEvent<HTMLInputElement>): void {
		const numRegex = /[0-9.]{1}/;
		const event = e.nativeEvent as any;

		// Single Character Scenario
		if (event.data?.length === 1) {
			// if invalid character, discard event
			if (!event.data.match(numRegex)) {
				(e.target as any).value = internalValue;
				return;
			}

			if (event.data === '.') {
				//Ignore additional decimal points
				if (internalValue.toString().includes('.'))
					(e.target as any).value = internalValue;
				setSuffix('00');
			}
		}
	}

	function extendOnchange(e: React.ChangeEvent<HTMLInputElement>): void {
		handleOnChange(e);
		if (props.onChange) props.onChange(e);
	}

	let decoratorStyle = {};
	if (variant === 'percent') {
		decoratorStyle = showPercentStyle;
	} else if (variant === 'currency') {
		decoratorStyle = showCurrencyStyle;
	}
	const assistiveTextId =
		assistiveText && (assistiveText.id || `${props.id}-hint` || 'amount-entry-id');
	return (
		<div className={cls(getRootClassListFromProps(classNames, props), className)}>
			<div className={classNames.inputContainer}>
				<div className={classNames.inputDecorator} style={decoratorStyle}>
					<div className={classNames.inputSpacer}>{internalValue}</div>
					<span className={classNames.suffixContainer}>
						<span className={classNames.suffix}>{suffix}</span>
					</span>
				</div>
				<input
					type="tel"
					id={props.id}
					pattern="[0-9.]+"
					value={internalValue}
					onInput={(e) => handleOnInput(e)}
					onChange={(e) => extendOnchange(e)}
					aria-label={props.label}
					aria-invalid={assistiveText && assistiveText.variant === 'error'}
					aria-describedby={assistiveTextId}
				/>
			</div>

			{assistiveText && (
				<AssistiveText
					className={classNames.assistiveText}
					{...{ ...assistiveText, ...(assistiveTextId && { id: assistiveTextId }) }}
				/>
			)}
		</div>
	);
};

AmountEntryXl.displayName = 'AmountEntryXl';

export default AmountEntryXl;
